/* ==========================================================================
   GOODS SCENE -- MIXED-CONTROL VARIANT PICKER

   <gs-variant-picker> lets ONE product show a dropdown for a 6-value option
   and pills for a 2-value option at the same time. Dawn cannot: it picks one
   wrapper for the whole product and each wrapper class reads only its own
   control type.

       VariantSelects.updateOptions()  ->  querySelectorAll('select')   only
       VariantRadios.updateOptions()   ->  querySelectorAll('fieldset') only

   Mixing them under either class produces a SHORT, INDEX-SHIFTED options array,
   updateMasterId() never matches a variant, and the page falls into
   setUnavailable(): price, inventory and SKU vanish and Add to Cart locks.

   🛑 assets/global.js IS NOT MODIFIED. This class INHERITS VariantSelects and
   overrides exactly TWO methods. Everything else -- updateMasterId, updateMedia,
   updateURL, updateVariantInput, renderProductInfo, toggleAddButton,
   setUnavailable, getVariantData, the variantChange event -- is Dawn's, unchanged.

   🔑 updateVariantStatuses() IS DELIBERATELY *NOT* OVERRIDDEN. It was audited and
   is already mixed-safe: `.product-form__input` sits on BOTH the fieldset and the
   dropdown div, and `:checked` matches BOTH a checked radio AND a selected
   <option>. Dawn's progressive-availability logic therefore works across mixed
   controls untouched. Do not "fix" it.
   ========================================================================== */

(function () {
  /* ⚠️ `class X extends VariantSelects` THROWS AT DEFINITION TIME if the base class
     is missing, which would kill this whole file. global.js is deferred in <head>
     and this asset is deferred in <body>, so document order guarantees it has
     run -- but a future load-order change must fail loudly here, not silently
     leave an undefined custom element on the page. */
  if (typeof VariantSelects === 'undefined') {
    console.warn('[gs-variant-picker] VariantSelects is not defined; global.js must load first.');
    return;
  }

  if (customElements.get('gs-variant-picker')) return;

  class GsVariantPicker extends VariantSelects {
    /* 🛑 SELF-HEALING FOR QUICK ADD. quick-add.js hunts for the OLD tag names:

         preventVariantURLSwitching() -> querySelector('variant-radios,variant-selects')
         preventDuplicatedIDs()       -> querySelectorAll('variant-selects, variant-radios, product-info')

       Neither matches <gs-variant-picker>, so in the Quick Add modal Dawn would
       fail to set two attributes, with two very different costs:

         data-update-url      missing -> the modal rewrites the browser URL
         data-original-section missing -> renderProductInfo() fetches
                                          section_id=quickadd-<id>, which does not
                                          exist, so price and inventory NEVER
                                          update inside the modal

       Rather than edit Dawn's quick-add.js, the element restores both itself.
       ⚠️ THIS COUPLES TO ONE STRING: quick-add.js builds the id as
       `quickadd-${sectionId}` in preventDuplicatedIDs(). If that prefix ever
       changes, this block goes stale silently -- and the symptom would be a
       modal whose price stops updating. */
    connectedCallback() {
      const section = this.dataset.section || '';
      if (section.indexOf('quickadd-') !== 0) return;
      if (!this.dataset.originalSection) {
        this.dataset.originalSection = section.slice('quickadd-'.length);
      }
      this.dataset.updateUrl = 'false';
    }

    /* 🔑 OPTION ORDER COMES FROM data-gs-option-position, NEVER FROM THE DOM OR THE
       CONTROL TYPE. updateMasterId() compares this.options[index] against
       variant.options[index] positionally, so one wrong index silently resolves
       the WRONG variant rather than failing -- the worst possible failure mode for
       a shop. Each wrapper carries its Liquid option.position and the array is
       built by sorting on it.

       ⚠️ NULL-SAFE, UNLIKE DAWN. VariantRadios does
           Array.from(fieldset.querySelectorAll('input')).find((r) => r.checked).value
       with no guard: a fieldset with nothing checked throws a TypeError and aborts
       the entire variant-change pipeline. Here a missing selection yields null,
       updateMasterId() simply finds no variant, and Dawn's own setUnavailable()
       path takes over cleanly. */
    updateOptions() {
      const wrappers = Array.from(this.querySelectorAll('[data-gs-option-position]')).sort(
        (a, b) =>
          parseInt(a.dataset.gsOptionPosition, 10) - parseInt(b.dataset.gsOptionPosition, 10)
      );

      this.options = wrappers.map((wrapper) => {
        const select = wrapper.querySelector('select');
        if (select) return select.value;

        const checked = wrapper.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    /* 🛑 THE SECOND OVERRIDE, AND THE ONE THAT IS EASY TO MISS. Dawn ships TWO
       incompatible implementations of this method and picks one per class:

         VariantSelects -> input.innerText = ... unavailable_with_option
         VariantRadios  -> input.classList.toggle('disabled')

       updateVariantStatuses() collects `input[type="radio"], option` together, so
       in a mixed picker ONE strategy is wrong for half the elements. Inheriting
       the VariantSelects version would write innerText onto radio inputs -- which
       have no text -- and never add `.disabled`, leaving IMPOSSIBLE COMBINATIONS
       LOOKING SELECTABLE. Branching by tagName keeps each control type on the
       behaviour Dawn designed for it. */
    setInputAvailability(listOfOptions, listOfAvailableOptions) {
      listOfOptions.forEach((input) => {
        const available = listOfAvailableOptions.includes(input.getAttribute('value'));

        if (input.tagName === 'OPTION') {
          input.innerText = available
            ? input.getAttribute('value')
            : window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
          return;
        }

        input.classList.toggle('disabled', !available);
      });
    }
  }

  customElements.define('gs-variant-picker', GsVariantPicker);
})();
